import { HttpClient } from "@angular/common/http";
import { Injectable, computed, inject, signal } from "@angular/core";
import { firstValueFrom } from "rxjs";

import {
  validateBadges,
  validateLevels,
  validateManeuvers,
  validateRanks,
  validateScoring,
} from "./config.validators";
import type {
  BadgesConfig,
  GamificationConfig,
  LevelsConfig,
  ManeuversConfig,
  RanksConfig,
  ScoringConfig,
} from "./gamification.types";

const CONFIG_BASE = "/gamification";

interface ConfigState {
  readonly maneuvers: ManeuversConfig | null;
  readonly scoring: ScoringConfig | null;
  readonly ranks: RanksConfig | null;
  readonly badges: BadgesConfig | null;
  readonly levels: LevelsConfig | null;
  readonly error: string | null;
}

const EMPTY_STATE: ConfigState = {
  maneuvers: null,
  scoring: null,
  ranks: null,
  badges: null,
  levels: null,
  error: null,
};

/**
 * Loads, validates and exposes the gamification JSON configs as signals.
 *
 * Designed to be lazy: call {@link load} once before the Practice UI mounts;
 * subsequent calls return the cached promise so it's safe to call from any
 * route. Validation errors surface through `error()` and as a rejected
 * promise — callers should render a user-visible message when the configs
 * fail to parse rather than silently falling back to defaults.
 */
@Injectable({ providedIn: "root" })
export class GamificationConfigService {
  private readonly http = inject(HttpClient);
  private readonly state = signal<ConfigState>(EMPTY_STATE);
  private loadPromise: Promise<GamificationConfig> | null = null;

  readonly maneuvers = computed(() => this.state().maneuvers);
  readonly scoring = computed(() => this.state().scoring);
  readonly ranks = computed(() => this.state().ranks);
  readonly badges = computed(() => this.state().badges);
  readonly levels = computed(() => this.state().levels);
  readonly error = computed(() => this.state().error);
  readonly isLoaded = computed(() => this.state().maneuvers !== null);

  load(): Promise<GamificationConfig> {
    if (this.loadPromise) return this.loadPromise;
    this.loadPromise = this.fetchAndValidate();
    return this.loadPromise;
  }

  private async fetchAndValidate(): Promise<GamificationConfig> {
    try {
      const [rawManeuvers, rawScoring, rawRanks, rawBadges, rawLevels] =
        await Promise.all([
          firstValueFrom(
            this.http.get<unknown>(`${CONFIG_BASE}/maneuvers.json`),
          ),
          firstValueFrom(this.http.get<unknown>(`${CONFIG_BASE}/scoring.json`)),
          firstValueFrom(this.http.get<unknown>(`${CONFIG_BASE}/ranks.json`)),
          firstValueFrom(this.http.get<unknown>(`${CONFIG_BASE}/badges.json`)),
          firstValueFrom(this.http.get<unknown>(`${CONFIG_BASE}/levels.json`)),
        ]);
      const config: GamificationConfig = {
        maneuvers: validateManeuvers(rawManeuvers),
        scoring: validateScoring(rawScoring),
        ranks: validateRanks(rawRanks),
        badges: validateBadges(rawBadges),
        levels: validateLevels(rawLevels),
      };
      this.state.set({ ...config, error: null });
      return config;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.state.set({ ...EMPTY_STATE, error: message });
      // Drop the cached promise so a retry can be attempted.
      this.loadPromise = null;
      throw err;
    }
  }
}
